import { NextGenUiToggle } from '@components/NextGenUiToggle';
import { useConfigContext } from '@fe-core';
import {
  Navigate,
  Route,
  RouteProps,
  Routes,
  useParams,
} from 'react-router-dom';
import { OverviewTab } from '@components/tabs/overview/OverviewTab';
import { InstructorsTab } from '@components/tabs/InstructorsTab';
import { ParticipantsTab } from '@components/tabs/ParticipantsTab';
import { EventsTab } from '@components/tabs/events/EventsTab';
import { SelfRegPage } from '@routes/selfreg/SelfRegPage';
import { useSelfregSessionDetails } from './hooks/data/useSelfregSessionDetails';

export type TTabKeys = 'overview' | 'participants' | 'instructors' | 'events';
type TTabRoute = RouteProps & {
  enabled?: boolean;
};

export const SelfRegRoute = () => {
  const { sessionId } = useParams();
  const { extension } = useConfigContext();
  const hiddenTabs = extension?.['fe-learner-catalog']?.hiddenTabs ?? [];

  const { data: sessionDetails } = useSelfregSessionDetails({
    enabled: true,
    sessionId,
  });

  if (!sessionDetails) {
    return null;
  }

  const tabRoutes: Record<TTabKeys, TTabRoute> = {
    overview: {
      enabled: true,
      path: 'overview',
      element: <OverviewTab sessionDetails={sessionDetails} />,
    },
    instructors: {
      enabled: true,
      path: 'instructors',
      element: <InstructorsTab persons={sessionDetails.trainers} />,
    },
    participants: {
      enabled:
        sessionDetails.participantInfo?.enabled &&
        sessionDetails.participantInfo?.showThumbnail,
      path: 'participants',
      element: <ParticipantsTab persons={sessionDetails.participants} />,
    },
    events: {
      enabled: true,
      path: 'events',
      element: <EventsTab dates={sessionDetails.dates} />,
    },
  };

  const availableTabs = Object.entries(tabRoutes).filter(
    ([tab, route]) => route.enabled && !hiddenTabs.includes(tab),
  );
  const availableTabKeys = availableTabs.map(([tab]) => tab as TTabKeys);
  const [firstAvailableTabKey] = availableTabKeys;

  return (
    <Routes>
      <Route
        element={
          <>
            <NextGenUiToggle isLoading={false} />
            <SelfRegPage
              tabKeys={availableTabKeys}
              sessionDetails={sessionDetails}
            />
          </>
        }
      >
        {availableTabs.map(([tabKey, route]) => (
          <Route key={tabKey} {...route} />
        ))}
      </Route>
      <Route
        path="*"
        element={<Navigate to={firstAvailableTabKey} replace />}
      />
    </Routes>
  );
};
